<script>
import { ref } from 'vue';
// composable
import { useConfig } from 'dashboard/composables/useConfig';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAI } from 'dashboard/composables/useAI';
import { useMapGetter } from 'dashboard/composables/store';

// components
import ReplyBox from './ReplyBox.vue';
import Message from './Message.vue';
import ActivityMessageGroup from './ActivityMessageGroup.vue';
import NextMessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import ScheduledMessagesList from './ScheduledMessagesList.vue';
import useScheduledMessagesChannel from 'dashboard/composables/useScheduledMessagesChannel';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
  separateReactions,
} from 'dashboard/helper/conversationHelper';
import { groupActivityMessages } from 'shared/helpers/messageGroupingHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { FEATURE_FLAGS } from '../../../featureFlags';

export default {
  components: {
    Message,
    ActivityMessageGroup,
    NextMessageList,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    ScheduledMessagesList,
  },
  mixins: [inboxMixin],
  props: {
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['contactPanelToggle'],
  setup() {
    const isPopOutReplyBox = ref(false);
    const { isEnterprise } = useConfig();

    const closePopOutReplyBox = () => {
      isPopOutReplyBox.value = false;
    };

    const showPopOutReplyBox = () => {
      isPopOutReplyBox.value = !isPopOutReplyBox.value;
    };

    const keyboardEvents = {
      Escape: {
        action: closePopOutReplyBox,
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
    } = useAI();

    const {
      subscribeToScheduledMessages,
      unsubscribeFromScheduledMessages,
      resetSubscriptions: resetScheduledMessagesSubscriptions,
    } = useScheduledMessagesChannel();

    const currentAccountId = useMapGetter('getCurrentAccountId');
    const isFeatureEnabledonAccount = useMapGetter(
      'accounts/isFeatureEnabledonAccount'
    );

    const showNextBubbles = isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CHATWOOT_V4
    );
    // const showNextBubbles = true; // Force v4 UI

    return {
      isEnterprise,
      isPopOutReplyBox,
      closePopOutReplyBox,
      showPopOutReplyBox,
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
      showNextBubbles,
      subscribeToScheduledMessages,
      unsubscribeFromScheduledMessages,
      resetScheduledMessagesSubscriptions,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showScheduledMessages: false,
      scheduledSubscriptionConversationId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
      scheduledMessagesGetter: 'scheduledMessages/getScheduledMessagesForConversation',
    }),
    scheduledMessagesForConversation() {
      // Use database ID for filtering scheduled messages
      const idToFilter = this.currentChat.database_id || this.currentChat.id;
      if (!this.scheduledMessagesGetter || !idToFilter) {
        return [];
      }
      const allMessages = this.scheduledMessagesGetter(idToFilter);
      const filtered = allMessages?.filter(
        msg => msg.status === 'pending' || msg.status === 'failed'
      ) || [];
      return filtered;
    },
    hasScheduledMessages() {
      return this.scheduledMessagesForConversation.length > 0;
    },
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.isEnterprise &&
        this.isAIIntegrationEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      const inbox = this.$store.getters['inboxes/getInbox'](this.inboxId);
      console.debug('[MessagesView] inbox lookup', {
        inboxId: this.inboxId,
        channelType: inbox?.channel_type,
        provider: inbox?.provider,
        medium: inbox?.medium,
      });
      return inbox;
    },
    isEvolutionInbox() {
      const integrationType = this.$store.getters['inboxMetadata/getInboxIntegrationType'](this.inboxId);
      return integrationType === 'evolution';
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    messagesAndReactions() {
      return separateReactions(this.getMessages);
    },
    filteredMessages() {
      return this.messagesAndReactions.regularMessages;
    },
    reactionsMap() {
      return this.messagesAndReactions.reactionsMap;
    },
    readMessages() {
      return getReadMessages(
        this.filteredMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.filteredMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    groupedReadMessages() {
      return groupActivityMessages(this.readMessages);
    },
    groupedUnReadMessages() {
      return groupActivityMessages(this.unReadMessages);
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },

    isATweet() {
      return this.conversationType === 'tweet';
    },
    isRightOrLeftIcon() {
      if (this.isContactPanelOpen) {
        return 'arrow-chevron-right';
      }
      return 'arrow-chevron-left';
    },
    getLastSeenAt() {
      const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
      return contactLastSeenAt;
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
          } = additionalAttributes;
          return agentReplyTimeWindowMessage;
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAWhatsAppChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }

      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchScheduledMessages();
      this.subscribeScheduledMessagesChannel();
      // this.fetchSuggestions(); // Commented out - not being used
      this.messageSentSinceOpened = false;
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a new message comes in, we refetch the label suggestions
    emitter.on(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
    emitter.on(
      BUS_EVENTS.OUTGOING_STATUS_UPDATED,
      this.handleOutgoingStatusUpdated
    );
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchScheduledMessages();
    this.subscribeScheduledMessagesChannel();
    // this.fetchSuggestions(); // Commented out - not being used
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    this.resetScheduledMessagesSubscriptions();
  },

  methods: {
    async fetchScheduledMessages() {
      if (!this.currentChat.id) return;
      try {
        await this.$store.dispatch(
          'scheduledMessages/fetchForConversation',
          {
            conversationId: this.currentChat.id,
            scheduleableId: this.currentChat.database_id || this.currentChat.id,
          }
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch scheduled messages', error);
      }
    },
    toggleScheduledMessages() {
      this.showScheduledMessages = !this.showScheduledMessages;
    },
    subscribeScheduledMessagesChannel() {
      const convId = this.currentChat.database_id || this.currentChat.id;
      if (!convId) return;

      if (
        this.scheduledSubscriptionConversationId &&
        this.scheduledSubscriptionConversationId !== convId
      ) {
        this.unsubscribeFromScheduledMessages(
          this.scheduledSubscriptionConversationId
        );
      }

      this.subscribeToScheduledMessages(convId, payload => {
        this.handleScheduledMessageCable(payload);
      });
      this.scheduledSubscriptionConversationId = convId;
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      if (!this.isEnterprise) {
        return;
      }

      // method available in mixin, need to ensure that integrations are present
      await this.fetchIntegrationsIfRequired();

      if (!this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.fetchLabelSuggestions({
        conversationId: this.currentChat.id,
      });

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
      emitter.off(
        BUS_EVENTS.OUTGOING_STATUS_UPDATED,
        this.handleOutgoingStatusUpdated
      );
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    onToggleContactPanel() {
      this.$emit('contactPanelToggle');
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    handleOutgoingStatusUpdated({
      conversationId,
      conversationDatabaseId,
      status,
    }) {
      const matchesCurrentConversation =
        conversationId === this.currentChat.id ||
        conversationDatabaseId === this.currentChat.database_id ||
        conversationDatabaseId === this.currentChat.id;
      if (!matchesCurrentConversation) return;

      if (
        [MESSAGE_STATUS.SENT, MESSAGE_STATUS.FAILED].includes(status)
      ) {
        this.fetchScheduledMessages();
        this.messageSentSinceOpened = true;
      }
    },
    handleScheduledMessageCable(payload) {
      const scheduleableId = Number(payload?.scheduleable_id);
      const targetScope = payload?.target_scope;
      const convDbId = this.currentChat.database_id || this.currentChat.id;

      if (
        !scheduleableId ||
        scheduleableId !== Number(convDbId) ||
        targetScope !== 'conversation'
      ) {
        return;
      }

      this.fetchScheduledMessages();
    },
    getInReplyToMessage(parentMessage) {
      if (!parentMessage) return {};
      const inReplyToMessageId = parentMessage.content_attributes?.in_reply_to;
      if (!inReplyToMessageId) return {};

      return this.currentChat?.messages.find(message => {
        if (message.id === inReplyToMessageId) {
          return true;
        }
        return false;
      });
    },
    openScheduleModal() {
      this.$refs.replyBox?.openScheduleModal?.();
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mt-2 mx-2 rounded-lg overflow-hidden"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <!-- <div class="flex justify-end">
      <woot-button
        variant="smooth"
        size="tiny"
        color-scheme="secondary"
        class="box-border fixed z-10 bg-white border border-r-0 border-solid rounded-bl-calc rtl:rotate-180 rounded-tl-calc border-n-weak"
        :class="isInboxView ? 'top-52 md:top-40' : 'top-32'"
        :icon="isRightOrLeftIcon"
        @click="onToggleContactPanel"
      />
    </div> -->
    <NextMessageList
      v-if="showNextBubbles"
      class="conversation-panel"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :is-a-whatsapp-channel="isAWhatsAppChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
          :is-evolution-inbox="isEvolutionInbox"
      :messages="getMessages"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li class="min-h-[4rem]">
            <span v-if="shouldShowSpinner" class="spinner message" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li v-show="unreadMessageCount != 0" class="unread--toast">
          <span>
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </NextMessageList>
    <ul v-else class="conversation-panel">
      <transition name="slide-up">
        <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
        <li class="min-h-[4rem]">
          <span v-if="shouldShowSpinner" class="spinner message" />
        </li>
      </transition>
      <template v-for="item in groupedReadMessages" :key="item.id || item.id">
        <ActivityMessageGroup
          v-if="item.isGroup"
          :messages="item.messages"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :is-a-facebook-inbox="isAFacebookInbox"
          :is-an-email-inbox="isAnEmailChannel"
          :is-instagram="isInstagramDM"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :is-evolution-inbox="isEvolutionInbox"
        />
        <Message
          v-else
          class="message--read ph-no-capture"
          data-clarity-mask="True"
          :data="item"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :is-a-facebook-inbox="isAFacebookInbox"
          :is-an-email-inbox="isAnEmailChannel"
          :is-instagram="isInstagramDM"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :is-evolution-inbox="isEvolutionInbox"
          :in-reply-to="getInReplyToMessage(item)"
          :reactions="reactionsMap[item.id] || []"
        />
      </template>
      <li v-show="unreadMessageCount != 0" class="unread--toast">
        <span>
          {{ unreadMessageCount > 9 ? '9+' : unreadMessageCount }}
          {{
            unreadMessageCount > 1
              ? $t('CONVERSATION.UNREAD_MESSAGES')
              : $t('CONVERSATION.UNREAD_MESSAGE')
          }}
        </span>
      </li>
      <template v-for="item in groupedUnReadMessages" :key="item.id || item.id">
        <ActivityMessageGroup
          v-if="item.isGroup"
          :messages="item.messages"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :is-a-facebook-inbox="isAFacebookInbox"
          :is-an-email-inbox="isAnEmailChannel"
          :is-instagram="isInstagramDM"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :is-evolution-inbox="isEvolutionInbox"
        />
        <Message
          v-else
          class="message--unread ph-no-capture"
          data-clarity-mask="True"
          :data="item"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :is-a-facebook-inbox="isAFacebookInbox"
          :is-instagram-dm="isInstagramDM"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :is-evolution-inbox="isEvolutionInbox"
          :in-reply-to="getInReplyToMessage(item)"
          :reactions="reactionsMap[item.id] || []"
        />
      </template>
      <ConversationLabelSuggestion
        v-if="shouldShowLabelSuggestions"
        :suggested-labels="labelSuggestions"
        :chat-labels="currentChat.labels"
        :conversation-id="currentChat.id"
      />
    </ul>

    <!-- Scheduled Messages Section -->
    <transition name="slide-down">
      <div
        v-if="hasScheduledMessages"
        class="scheduled-messages-section border-t border-n-weak bg-n-solid-1"
      >
        <!-- Collapsible Header -->
        <button
          class="w-full flex items-center justify-between px-4 py-3 hover:bg-n-alpha-2 transition-colors"
          @click="toggleScheduledMessages"
        >
          <div class="flex items-center gap-2">
            <i class="i-lucide-calendar-clock text-n-slate-11"></i>
            <span class="text-sm font-medium text-n-slate-12">
              Mensagens Agendadas
            </span>
            <span class="px-2 py-0.5 text-xs rounded-full bg-n-iris-9 text-white font-medium">
              {{ scheduledMessagesForConversation.length }}
            </span>
          </div>
          <i
            :class="[
              'transition-transform',
              showScheduledMessages ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
              'text-n-slate-11'
            ]"
          ></i>
        </button>

        <!-- Collapsible Content -->
        <transition name="expand">
          <div v-show="showScheduledMessages" class="px-4 pb-4 max-h-48 overflow-y-auto overflow-x-visible">
            <ScheduledMessagesList
              :conversation-id="currentChat.database_id || currentChat.id"
              :conversation-display-id="currentChat.id"
              @edit="openScheduleModal"
            />
          </div>
        </transition>
      </div>
    </transition>

    <div
      class="conversation-footer"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-background': showNextBubbles && !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-slate-700 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <ReplyBox
        ref="replyBox"
        v-model:popout-reply-box="isPopOutReplyBox"
        @toggle-popout="showPopOutReplyBox"
      />
    </div>
  </div>
</template>

<style scoped>
@tailwind components;

@layer components {
  .rounded-bl-calc {
    border-bottom-left-radius: calc(1.5rem + 1px);
  }

  .rounded-tl-calc {
    border-top-left-radius: calc(1.5rem + 1px);
  }
}
</style>

<style scoped lang="scss">
.modal-mask {
  @apply absolute;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute left-auto bottom-1;
    }
  }
}

/* Scheduled Messages Section Transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 320px;
  opacity: 1;
}
</style>
